import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectCreative } from 'swiper/modules';
import { 
  Section, 
  Card, 
  Button,
  FadeInUp,
  SlideIn,
  StaggerContainer,
  StaggerItem,
} from '../../../components/common';
import { usePageTitle } from '../../../hooks/usePageTitle';
import {
  PRIVATE_LESSONS_SLIDER,
  PRIVATE_LESSONS_FEATURES,
} from '../../../constants';

// Recursos exclusivos (usando imagens de PRIVATE_LESSONS_FEATURES)
const EXCLUSIVE_FEATURES = [
  {
    id: 'homework',
    icon: 'ri-book-open-line',
    title: 'Homeworks Personalizados',
    shortDesc: 'Exercícios feitos para você',
    fullDesc: 'Receba tarefas personalizadas baseadas nas suas dificuldades específicas. Cada homework é pensado para reforçar o que você aprendeu e preparar você para a próxima aula.',
    color: 'olive',
    image: PRIVATE_LESSONS_FEATURES[0].url
  },
  {
    id: 'cultural',
    icon: 'ri-music-2-line',
    title: 'Imersão Cultural',
    shortDesc: 'Músicas, séries e podcasts',
    fullDesc: 'Mergulhe no inglês através de músicas, séries, filmes e podcasts selecionados especialmente para o seu nível. Aprenda expressões reais enquanto se diverte.',
    color: 'sand',
    image: PRIVATE_LESSONS_FEATURES[1].url
  },
  {
    id: 'board',
    icon: 'ri-artboard-2-line',
    title: 'Board Interativo',
    shortDesc: 'Anotações digitais da aula',
    fullDesc: 'Todas as aulas ficam documentadas em um board digital compartilhado. Você pode revisar vocabulário, gramática e anotações sempre que precisar.',
    color: 'olive',
    image: PRIVATE_LESSONS_FEATURES[2].url
  },
  {
    id: 'support',
    icon: 'ri-question-answer-line',
    title: 'Suporte Contínuo',
    shortDesc: 'Tire dúvidas a qualquer hora',
    fullDesc: 'Não espere a próxima aula para tirar suas dúvidas. Mande áudios, textos ou perguntas pelo WhatsApp e receba feedback rápido do seu professor.',
    color: 'sand',
    image: PRIVATE_LESSONS_FEATURES[3].url
  },
];

// Situações reais de aprendizado
const REAL_SITUATIONS = [
  { icon: 'ri-plane-line', title: 'Viagens', desc: 'Check-in, pedidos, direções' },
  { icon: 'ri-briefcase-line', title: 'Trabalho', desc: 'Reuniões, emails, apresentações' },
  { icon: 'ri-restaurant-line', title: 'Restaurantes', desc: 'Pedidos, preferências, gorjetas' },
  { icon: 'ri-phone-line', title: 'Telefone', desc: 'Ligações, recados, agendamentos' },
  { icon: 'ri-shopping-bag-line', title: 'Compras', desc: 'Lojas, devoluções, descontos' },
  { icon: 'ri-hospital-line', title: 'Emergências', desc: 'Médico, farmácia, ajuda' },
];

export default function PrivateLessons() {
  usePageTitle('Aulas Particulares');
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const [hoveredSituation, setHoveredSituation] = useState<number | null>(null);

  const selectedFeature = EXCLUSIVE_FEATURES.find(f => f.id === activeFeature);

  return (
    <>
      {/* Hero com Slider */}
      <section className="relative min-h-screen">
        <Swiper
          modules={[Autoplay, Pagination, EffectCreative]}
          effect="creative"
          creativeEffect={{
            prev: { shadow: true, translate: ['-20%', 0, -1] },
            next: { translate: ['100%', 0, 0] },
          }}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          loop={true}
          className="absolute inset-0 w-full h-full"
        >
          {PRIVATE_LESSONS_SLIDER.map((photo) => (
            <SwiperSlide key={photo.id}>
              <div 
                className="absolute inset-0 w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${photo.url})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-earth-900/90 via-earth-900/50 to-earth-900/30" />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Hero Content */}
        <div className="relative z-10 min-h-screen flex items-center">
          <div className="container-custom pt-24 pb-24 sm:pb-32">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <span className="inline-flex items-center gap-2 bg-olive-600/90 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                <i className="ri-group-line"></i>
                Grupos de até 4 pessoas
              </span>
              
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Aulas que vão além<br />
                <span className="text-olive-400">da sala de aula</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-sand-200 mb-6 sm:mb-8 leading-relaxed max-w-2xl">
                Beyond Class é sobre vivenciar o idioma, abrir portas, realizar sonhos e transformar vidas. Aprenda inglês com imersão real: homeworks personalizados, recursos de imersão cultural, board digital para revisar e suporte para tirar dúvidas quando precisar.
              </p>

              {/* Quick Features */}
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8">
                {[
                  { icon: 'ri-book-open-line', text: 'Plano de estudo' },
                  { icon: 'ri-music-2-line', text: 'Imersão cultural' },
                  { icon: 'ri-artboard-2-line', text: 'Board digital' },
                  { icon: 'ri-book-2-line', text: 'Clube de leitura' },
                ].map((item) => (
                  <span 
                  key={item.text}
                  className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm border border-white/20"
                  >
                  <i className={`${item.icon} text-olive-400`}></i>
                  {item.text}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button to="/contato" size="lg" icon="ri-calendar-line">
                  Agendar aula experimental
                </Button>
                <Button 
                  href="#recursos" 
                  variant="outline" 
                  size="lg"
                  className="border-white/30 text-white hover:bg-white hover:text-earth-900"
                >
                  <i className="ri-arrow-down-line mr-2"></i>
                  Ver recursos exclusivos
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recursos Exclusivos - Design Interativo */}
      <Section bgColor="sand" id="recursos">
        <FadeInUp className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-star-line"></i>
            Exclusivo Beyond Class
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-earth-800 mb-4">
            Recursos que fazem a diferença
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-lg">
            Clique em cada recurso para descobrir como transformamos seu aprendizado
          </p>
        </FadeInUp>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Features List */}
          <div className="space-y-4">
            {EXCLUSIVE_FEATURES.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <motion.button
                  onClick={() => setActiveFeature(activeFeature === feature.id ? null : feature.id)}
                  className={`w-full text-left p-6 rounded-2xl transition-all duration-300 ${
                    activeFeature === feature.id 
                      ? 'bg-olive-600 text-white shadow-lg scale-[1.02]' 
                      : 'bg-white hover:bg-sand-50 border border-sand-200 hover:border-olive-300'
                  }`}
                  whileHover={{ scale: activeFeature === feature.id ? 1.02 : 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      activeFeature === feature.id ? 'bg-white/20' : 'bg-olive-100'
                    }`}>
                      <i className={`${feature.icon} text-2xl ${
                        activeFeature === feature.id ? 'text-white' : 'text-olive-600'
                      }`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${
                        activeFeature === feature.id ? 'text-white' : 'text-earth-800'
                      }`}>
                        {feature.title}
                      </h3>
                      <p className={`text-sm ${
                        activeFeature === feature.id ? 'text-white/80' : 'text-earth-500'
                      }`}>
                        {feature.shortDesc}
                      </p>
                    </div>
                    <i className={`ri-arrow-right-s-line text-2xl transition-transform ${
                      activeFeature === feature.id ? 'rotate-90 text-white' : 'text-earth-400'
                    }`}></i>
                  </div>
                </motion.button>
              </motion.div>
            ))}
          </div>

          {/* Feature Detail Panel */}
          <div className="lg:sticky lg:top-24">
            <AnimatePresence mode="wait">
              {selectedFeature ? (
                <motion.div
                  key={selectedFeature.id}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-xl"
                >
                  <div className="relative h-48 md:h-64">
                    <img 
                      src={selectedFeature.image} 
                      alt={selectedFeature.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-white">{selectedFeature.title}</h3>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <p className="text-earth-600 leading-relaxed text-sm sm:text-base md:text-lg">
                      {selectedFeature.fullDesc}
                    </p>
                    <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-sand-200">
                      <Button to="/contato" icon="ri-arrow-right-line" className="w-full">
                        Quero esse recurso
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-gradient-to-br from-olive-100 to-sand-100 rounded-2xl p-6 sm:p-8 text-center h-64 sm:h-80 md:h-96 flex flex-col items-center justify-center"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-olive-200 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className="ri-cursor-line text-2xl sm:text-3xl text-olive-600 animate-pulse"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-earth-800 mb-2">
                    Explore nossos recursos
                  </h3>
                  <p className="text-earth-600 text-sm sm:text-base">
                    Clique em um recurso ao lado para ver mais detalhes
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Section>

      {/* Situações Reais - Design Inovador */}
      <Section bgColor="white">
        <FadeInUp className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-earth-800 mb-3 sm:mb-4">
            Aprenda para a vida real
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-sm sm:text-base md:text-lg">
            Praticamos situações que você vai realmente usar. Nada de teoria sem aplicação.
          </p>
        </FadeInUp>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {REAL_SITUATIONS.map((situation, index) => (
            <motion.div
              key={situation.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              onMouseEnter={() => setHoveredSituation(index)}
              onMouseLeave={() => setHoveredSituation(null)}
              className="relative group"
            >
              <motion.div
                className={`p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl text-center cursor-pointer transition-all duration-300 ${
                  hoveredSituation === index 
                    ? 'bg-olive-600 text-white shadow-xl scale-105' 
                    : 'bg-sand-50 hover:bg-sand-100'
                }`}
                whileHover={{ y: -5 }}
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3 transition-colors ${
                  hoveredSituation === index ? 'bg-white/20' : 'bg-olive-100'
                }`}>
                  <i className={`${situation.icon} text-lg sm:text-xl md:text-2xl ${
                    hoveredSituation === index ? 'text-white' : 'text-olive-600'
                  }`}></i>
                </div>
                <h3 className={`font-semibold mb-0.5 sm:mb-1 text-sm sm:text-base ${
                  hoveredSituation === index ? 'text-white' : 'text-earth-800'
                }`}>
                  {situation.title}
                </h3>
                <p className={`text-[10px] sm:text-xs ${
                  hoveredSituation === index ? 'text-white/80' : 'text-earth-500'
                }`}>
                  {situation.desc}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Interactive CTA */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-3 bg-olive-50 rounded-full px-6 py-3 mb-6">
            <span className="flex h-3 w-3 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-olive-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-olive-500"></span>
            </span>
            <span className="text-olive-700 font-medium">Vagas abertas para novas turmas</span>
          </div>
        </motion.div>
      </Section>

      {/* Como Funciona - Timeline Visual */}
      <Section bgColor="olive">
        <FadeInUp className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-earth-800 mb-4">
            Sua jornada de aprendizado
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-lg">
            Do primeiro contato até a fluência, acompanhamos cada passo
          </p>
        </FadeInUp>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-olive-300 -translate-x-1/2" />
            
            {[
              { 
                step: '1', 
                icon: 'ri-chat-smile-2-line', 
                title: 'Bate-papo inicial', 
                desc: 'Conversamos sobre seus objetivos, interesses e disponibilidade. Sem compromisso!',
                side: 'left'
              },
              { 
                step: '2', 
                icon: 'ri-test-tube-line', 
                title: 'Avaliação de nível', 
                desc: 'Identificamos seu nível atual através de uma conversa natural e exercícios leves.',
                side: 'right'
              },
              { 
                step: '3', 
                icon: 'ri-map-2-line', 
                title: 'Plano personalizado', 
                desc: 'Criamos sua trilha de aprendizado com base nos seus objetivos e tempo disponível.',
                side: 'left'
              },
              { 
                step: '4', 
                icon: 'ri-rocket-2-line', 
                title: 'Início das aulas', 
                desc: 'Começamos as aulas com todo o suporte: board, homeworks, imersão cultural e mais!',
                side: 'right'
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: item.side === 'left' ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className={`relative flex items-center gap-6 mb-12 ${
                  item.side === 'right' ? 'md:flex-row-reverse' : ''
                }`}
              >
                {/* Step circle */}
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg z-10">
                  <div className="w-12 h-12 bg-olive-600 rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-white`}></i>
                  </div>
                </div>
                
                {/* Content */}
                <div className={`ml-28 md:ml-0 md:w-5/12 ${item.side === 'left' ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                  <Card className="relative">
                    <span className="absolute -top-3 left-4 bg-olive-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Passo {item.step}
                    </span>
                    <h3 className="text-lg font-semibold text-earth-800 mt-2 mb-2">{item.title}</h3>
                    <p className="text-earth-600 text-sm">{item.desc}</p>
                  </Card>
                </div>
                
                {/* Spacer for alternating layout */}
                <div className="hidden md:block md:w-5/12" />
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Depoimento / Social Proof */}
      <Section bgColor="white">
        <div className="max-w-4xl mx-auto">
          <FadeInUp>
            <div className="bg-gradient-to-br from-sand-50 to-olive-50 rounded-3xl p-8 md:p-12 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-4 right-4 text-8xl text-olive-200 opacity-50">
                <i className="ri-double-quotes-r"></i>
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div 
                        key={i}
                        className="w-12 h-12 rounded-full bg-olive-200 border-3 border-white flex items-center justify-center"
                      >
                        <i className="ri-user-smile-line text-olive-600"></i>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-earth-800">+500 alunos</p>
                    <p className="text-sm text-earth-500">já passaram por nossas aulas</p>
                  </div>
                </div>
                
                <blockquote className="text-xl md:text-2xl text-earth-700 font-light italic mb-6 leading-relaxed">
                  "O diferencial é que você realmente usa o inglês. Os homeworks, as músicas, 
                  o suporte constante... Não é só uma aula, é uma experiência completa de aprendizado."
                </blockquote>
                
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-yellow-500 text-xl"></i>
                    ))}
                    <span className="text-earth-600 ml-2">5.0 de satisfação</span>
                  </div>
                  <Button to="/contato" variant="outline" icon="ri-arrow-right-line">
                    Fazer parte dessa história
                  </Button>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </Section>

      {/* CTA Final */}
      <Section bgColor="dark">
        <FadeInUp className="text-center max-w-3xl mx-auto">
          <div className="w-20 h-20 bg-olive-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-rocket-2-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-sand-100 mb-4">
            Pronto para começar?
          </h2>
          <p className="text-sand-300 text-lg mb-8 leading-relaxed">
            Agende uma aula experimental gratuita e experimente na prática 
            todos os recursos que fazem a diferença no seu aprendizado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              to="/contato" 
              size="lg" 
              className="bg-olive-500 hover:bg-olive-400 text-white"
              icon="ri-calendar-check-line"
            >
              Agendar aula grátis
            </Button>
            <Button 
              href="https://wa.me/5581997160139?text=Olá! Tenho interesse nas aulas particulares de inglês." 
              variant="outline" 
              size="lg"
              className="border-sand-300 text-sand-200 hover:bg-sand-200 hover:text-earth-900"
              icon="ri-whatsapp-line"
            >
              Falar no WhatsApp
            </Button>
          </div>
        </FadeInUp>
      </Section>
    </>
  );
}
